import Group from 'components/utils/Group'
import React, { MouseEventHandler, PropsWithChildren, useCallback, useContext } from 'react'
import AccommodationAmenitiesAndPoliciesModal from './AccommodationAmenitiesAndPoliciesModal'
import ModalContext from 'contexts/ModalContext'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TextButton from 'components/Luxkit/Button/TextButton'
import AccommodationRoomTileMessage from './AccommodationRoomTileMessage'
import WalledContent from 'components/Common/WalledContent'
import { useAppDispatch } from 'hooks/reduxHooks'
import { accountAccessShowLogin } from 'actions/UiActions'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'

export type AccommodationRoomTileActionsState =
  'sold-out-on-dates' |
  'sold-out' |
  'exceeded-capacity' |
  'sold-out-on-dates-or-exceeded-capacity' |
  'available-on-dates'

interface Props {
  roomPackage: App.HotelPackage
  state?: AccommodationRoomTileActionsState
  isWalled?: boolean
  offer: App.HotelOffer
  onAddAnotherUnitClick?: MouseEventHandler<HTMLButtonElement>
  onChangeDatesClick?: MouseEventHandler<HTMLButtonElement>
  onCheckAvailabilityClick?: MouseEventHandler<HTMLButtonElement>
}

function AccommodationRoomTileActions(props: PropsWithChildren<Props>) {
  const {
    children,
    roomPackage,
    state,
    offer,
    isWalled,
    onAddAnotherUnitClick,
    onCheckAvailabilityClick,
    onChangeDatesClick,
  } = props

  const appDispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const openAmenitiesAndPoliciesModal = useCallback(() => {
    showModal(<AccommodationAmenitiesAndPoliciesModal roomPackage={roomPackage} />)
  }, [roomPackage, showModal])

  return <Group direction="vertical" tabletHorizontalAlign="end" gap={12}>
    <CSSBreakpoint max="mobile">
      <TextButton outdent="left" kind="tertiary" onClick={openAmenitiesAndPoliciesModal}>
        View amenities & policies
      </TextButton>
    </CSSBreakpoint>

    <WalledContent
      enforced={isWalled ?? offer.walledGarden}
      wall={<AccommodationRoomTileMessage message="members-exclusive" />}
    >
      {!!state && <AccommodationRoomTileMessage message={state} />}
    </WalledContent>

    <Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="end" gap={8} wrap="wrap" noDisplayWhenEmpty>
      <CSSBreakpoint min="tablet">
        <TextButton kind="tertiary" onClick={openAmenitiesAndPoliciesModal}>
          View amenities & policies
        </TextButton>
      </CSSBreakpoint>
      <WalledContent
        enforced={isWalled ?? offer.walledGarden}
        wall={<TextButton kind="secondary" onClick={() => appDispatch(accountAccessShowLogin())}>
          Sign in or Become a member
        </TextButton>}
      >
        {children}
        {(state === 'sold-out-on-dates' || state === 'sold-out-on-dates-or-exceeded-capacity') && onChangeDatesClick && <TextButton
          data-testid="room-tile-action-change-dates"
          kind="secondary"
          onClick={onChangeDatesClick}
        >
          Change your dates
        </TextButton>}
        {(state === 'exceeded-capacity' || state === 'sold-out-on-dates-or-exceeded-capacity') && onAddAnotherUnitClick && <TextButton
          data-testid="room-tile-action-add-another-unit"
          kind="secondary"
          onClick={onAddAnotherUnitClick}
        >
          Add another {offer.saleUnit}
        </TextButton>}
        {state === 'available-on-dates' && onCheckAvailabilityClick && <TextButton
          data-testid="room-tile-action-check-availability"
          kind="primary"
          onClick={onCheckAvailabilityClick}
        >
          Check availability
        </TextButton>}
      </WalledContent>
    </Group>
  </Group>
}

export default withOfferPageAndReduxState((offerPageState) => ({
  offer: offerPageState.offer!,
}))(AccommodationRoomTileActions)
